import { useState, useEffect, useCallback } from 'react';

/**
 * useEmailPreview - Hook pour gérer l'aperçu d'email avec PDF
 *
 * @param {boolean} isOpen - État d'ouverture de la modale
 * @param {Object} facture - La facture à envoyer
 * @param {Function} onConfirm - Callback de confirmation d'envoi
 */
export function useEmailPreview(isOpen, facture, onConfirm) {
  const [sending, setSending] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);

  const numeroFacture = facture?.fields?.['N° facture'] || '';

  // Charger le PDF preview
  const loadPdfPreview = useCallback(async () => {
    if (!numeroFacture) return;

    setLoadingPdf(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const dateEmission = facture?.fields?.['Date émission'];

      // Import dynamique de VERCEL_FUNCTIONS
      const { VERCEL_FUNCTIONS } = await import('../config/vercel');

      const response = await fetch(VERCEL_FUNCTIONS.PREVIEW_FACTURE_PDF, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          numeroFacture,
          dateEmission: dateEmission || null
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      } else {
        console.error('Erreur lors du chargement du PDF preview');
      }
    } catch (error) {
      console.error('Erreur preview PDF:', error);
    } finally {
      setLoadingPdf(false);
    }
  }, [numeroFacture, facture]);

  // Charger au montage
  useEffect(() => {
    if (isOpen && numeroFacture) {
      loadPdfPreview();
    }
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, numeroFacture]);

  // Handler d'envoi
  const handleSend = useCallback(async () => {
    setSending(true);
    try {
      await onConfirm();
    } finally {
      setSending(false);
    }
  }, [onConfirm]);

  // Handler pour voir le PDF
  const handleViewPdf = useCallback(() => {
    setShowPdfViewer(true);
  }, []);

  // Handler pour fermer le PDF viewer
  const handleClosePdfViewer = useCallback(() => {
    setShowPdfViewer(false);
  }, []);

  return {
    sending,
    pdfUrl,
    loadingPdf,
    showPdfViewer,
    handleSend,
    handleViewPdf,
    handleClosePdfViewer
  };
}
